import { create } from "zustand"

import {
  GetCostBreakdownGoodsReceiptListDocument,
  type GetCostBreakdownGoodsReceiptListQuery,
  type GoodsReceipt,
  type GoodsReceiptSortField,
  GoodsReceiptStatus,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type ProductCostBreakdownGoodsReceiptsSearchFilters = SearchFilters & {
  productId: number
  field?: GoodsReceiptSortField
  order?: SortingOrder
}

const deduplicateGoodsReceipts = (newData: GoodsReceipt[], existingData: GoodsReceipt[]): GoodsReceipt[] => {
  const uniqueIds = new Set(existingData.map((receipt) => receipt.goodsReceiptId))
  return [...existingData, ...newData.filter((receipt) => !uniqueIds.has(receipt.goodsReceiptId))]
}

export const useProductCostBreakdownGoodsReceiptsSearchStore = create(
  createSearchStore<
    GoodsReceipt,
    ProductCostBreakdownGoodsReceiptsSearchFilters,
    GetCostBreakdownGoodsReceiptListQuery
  >({
    documentNode: GetCostBreakdownGoodsReceiptListDocument,
    deduplicateData: deduplicateGoodsReceipts,
    getQueryInput: (filters: ProductCostBreakdownGoodsReceiptsSearchFilters, offset: number, limit: number) => {
      const { field, order, ...otherFilters } = filters
      return {
        filter: {
          ...otherFilters,
          status: [GoodsReceiptStatus.CheckedIn, GoodsReceiptStatus.Stored],
        },
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.goodsReceipt.list.__typename,
      data:
        data.goodsReceipt.list.__typename === "GoodsReceiptListSuccess"
          ? (data.goodsReceipt.list.goodsReceipts as GoodsReceipt[])
          : [],
      error: data.goodsReceipt.list.__typename === "GoodsReceiptListFailure" ? data.goodsReceipt.list.error : undefined,
    }),
  })
)
